import { type ReactNode } from 'react'
import styles from './JobTopBar.module.css'
import { useHoverState } from '@/hooks/element-hooks'
import { Dropdown, DropdownScroll, DropdownTitle } from '@/components/Dropdown'
import { IconButton } from '@/components/IconButton'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'

export interface JobTopBarProps {
  title: string
  shortId: string
  isDetailsOpen: boolean
  onDetailsToggle: () => unknown
  callChip: ReactNode
  chatButton: ReactNode
  actions: ReactNode
  onJobListSidebarToggle?: () => unknown
  onJobListHoverChange: (isHovering: boolean) => unknown
  isJobListSidebarVisible: boolean
  onPreviousJob?: () => unknown
  onNextJob?: () => unknown
}

/**
 * The job's toolbar: the panes it can open on either side, which job this is,
 * and what can be done to it.
 */
export function JobTopBar({
  title,
  shortId,
  isDetailsOpen,
  onDetailsToggle,
  callChip,
  chatButton,
  actions,
  onJobListSidebarToggle,
  onJobListHoverChange,
  isJobListSidebarVisible,
  onPreviousJob,
  onNextJob,
}: JobTopBarProps) {
  const { connectRef: toggleRef } = useHoverState(onJobListHoverChange)
  return (
    <header className={styles.root}>
      {onJobListSidebarToggle && (
        <>
          <IconButton
            name="panel"
            label={isJobListSidebarVisible ? 'Hide my jobs' : 'Show my jobs'}
            variant="ghost"
            ref={toggleRef}
            aria-pressed={isJobListSidebarVisible}
            onPress={onJobListSidebarToggle}
            testId="job-list-toggle"
            size={15}
          />
          <span className={styles.divider} />
        </>
      )}
      <span className={styles.name} data-test="job-header-title">
        {title}
      </span>
      <span className={styles.shortId}>{shortId}</span>
      <span className={styles.spacer} />
      {callChip}
      {(onPreviousJob ?? onNextJob) && (
        <>
          <IconButton
            name="arrow"
            label="Previous job"
            variant="outline"
            isDisabled={!onPreviousJob}
            onPress={onPreviousJob}
            size={14}
            rotate={-90}
          />
          <IconButton
            name="arrow"
            label="Next job"
            variant="outline"
            isDisabled={!onNextJob}
            onPress={onNextJob}
            size={14}
            rotate={90}
          />
        </>
      )}
      {chatButton}
      {actions}
      <IconButton
        name="panel"
        label={isDetailsOpen ? 'Hide details panel' : 'Show details panel'}
        variant="ghost"
        aria-pressed={isDetailsOpen}
        onPress={onDetailsToggle}
        testId="job-details-toggle"
        size={15}
        rotate={180}
      />
    </header>
  )
}

export interface JobTopBarChatButtonProps {
  isOpen: boolean
  unreadCount: number
  onPress: () => unknown
}

export function JobTopBarChatButton({
  isOpen,
  unreadCount,
  onPress,
}: JobTopBarChatButtonProps) {
  return (
    <RawButton
      className={styles.button}
      aria-pressed={isOpen}
      onPress={onPress}
      testId="communication-tab-item-new"
    >
      <Icon name="chat-bubble" size={13} aria-hidden />
      Chat
      {unreadCount > 0 && <span className={styles.unread}>{unreadCount}</span>}
    </RawButton>
  )
}

export interface JobTopBarActionsProps {
  children: ReactNode
}

export function JobTopBarActions({ children }: JobTopBarActionsProps) {
  return (
    <div className={styles.actions}>
      <div className={styles.action}>
        <Dropdown
          autoDismiss
          width="small"
          position="bottom-end"
          button={
            <RawButton className={styles.button}>
              Actions
              <Icon name="arrow" size={14} rotate={90} aria-hidden />
            </RawButton>
          }
          testId="job-actions-dropdown"
          noBottomPadding
        >
          <DropdownTitle>Actions</DropdownTitle>
          <DropdownScroll autoHeightSize>{children}</DropdownScroll>
        </Dropdown>
      </div>
    </div>
  )
}
